import React from 'react';
import { Box, TableContainer, Table, TableHead, TableBody, CircularProgress, Typography, Checkbox } from '@mui/material';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { OrderPayload } from 'pages/myidm/store';
import { Styles, StyledTableCell, StyledTableRow } from 'pages/myidm/style';
import { CancelRequest } from 'pages/myidm/components/CancelRequest';
import { SubmitRequest } from 'pages/myidm/components/sections/ad-group-membership-removal/SubmitRequest';

const ADGroupMembersRemoval = () => {
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [selectedMembers, setSelectedMembers] = React.useState([]);
    const adGroupMembersApi = useMutation(() => axios.get(`${endPoints?.myIdm?.adGroupMembersApiV2}/${order?.adGroup}/${order?.domainType}`));
    const adGroupDetailsApi = useMutation(() => axios.get(`${endPoints.myIdm.adGroupDetailsApi}/${order?.adGroup}`));

    React.useEffect(() => {
        if (order?.adGroup && order?.domainType) {
            adGroupMembersApi?.mutate();
            adGroupDetailsApi?.mutate();
            setSelectedMembers([]); // Reset selection when AD group or domain type changes
        }
    }, [order?.adGroup, order?.domainType]);

    React.useEffect(() => {
        if (adGroupMembersApi?.isSuccess && adGroupMembersApi?.data?.data) {
            setOrder((prevOrder) => ({ ...prevOrder, adGroupMembers: adGroupMembersApi?.data?.data }));
        }
    }, [adGroupMembersApi?.isSuccess, adGroupMembersApi?.data]);

    React.useEffect(() => {
        if (adGroupDetailsApi?.isSuccess && adGroupDetailsApi?.data?.data) {
            setOrder((prevOrder) => ({ ...prevOrder, adGroupDetails: adGroupDetailsApi?.data?.data }));
        }
    }, [adGroupDetailsApi?.isSuccess, adGroupDetailsApi?.data]);

    React.useEffect(() => {
        // Update order with selected members for removal - map indices to actual member objects
        const selectedMemberObjects = selectedMembers.map((index) => order?.adGroupMembers?.[index]).filter(Boolean);
        setOrder((prevOrder) => ({ ...prevOrder, membersToRemove: selectedMemberObjects }));
    }, [selectedMembers, order?.adGroupMembers]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allMemberIndices = order?.adGroupMembers?.map((_, index) => index) || [];
            setSelectedMembers(allMemberIndices);
        } else {
            setSelectedMembers([]);
        }
    };

    const handleSelectMember = (index) => {
        setSelectedMembers((prev) => {
            if (prev.includes(index)) {
                return prev.filter((i) => i !== index);
            }
            return [...prev, index];
        });
    };

    const isAllSelected = order?.adGroupMembers?.length > 0 && selectedMembers.length === order?.adGroupMembers?.length;
    const isIndeterminate = selectedMembers.length > 0 && selectedMembers.length < (order?.adGroupMembers?.length || 0);

    return (
        <Box>
            <Typography variant="body2" sx={{ color: '#0047BA', mb: 1, fontSize: '16px', fontWeight: 700 }}>
                Current Group Members
            </Typography>
            <Box data-testid="members-removal-list" sx={{ width: '100%' }}>
                {adGroupMembersApi.isLoading && (
                    <Box sx={Styles?.addMembersCard?.loader}>
                        <CircularProgress color="inherit" size={16} />
                        <Typography variant="body2" sx={{ color: '#000000', mb: 1 }}>
                            Loading...
                        </Typography>
                    </Box>
                )}
                {adGroupMembersApi?.isError && !adGroupMembersApi?.isLoading && <Box sx={Styles?.addMembersCard?.wrongMsg}>Something is Wrong</Box>}
                {!adGroupMembersApi?.isError &&
                    !adGroupMembersApi?.isLoading &&
                    (adGroupMembersApi?.data?.data?.length === 0 || adGroupMembersApi?.data?.data === null || order?.adGroupMembers === null) && (
                        <Box sx={Styles?.addMembersCard?.nomembers}>No Members</Box>
                    )}
                {!adGroupMembersApi.isLoading && !adGroupMembersApi.isError && adGroupMembersApi?.data?.data?.length > 0 && order?.adGroupMembers && (
                    <>
                        <TableContainer sx={Styles?.AddMemberForm?.table.tableWrapper}>
                            <Table sx={{ ...Styles?.AddMemberForm?.table?.main, height: 'auto', borderSpacing: 0 }} size="small" stickyHeader>
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell sx={{ width: '50px', fontWeight: 600, backgroundColor: '#E5F5FC !important' }}>
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={isIndeterminate}
                                                onChange={handleSelectAll}
                                                inputProps={{ 'aria-label': 'select all members' }}
                                                sx={{ padding: 0 }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ fontWeight: 600, backgroundColor: '#E5F5FC !important' }}>GPID</StyledTableCell>
                                        <StyledTableCell sx={{ fontWeight: 600, backgroundColor: '#E5F5FC !important' }}>Name</StyledTableCell>
                                        <StyledTableCell sx={{ fontWeight: 600, backgroundColor: '#E5F5FC !important' }}>Email</StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {order?.adGroupMembers?.map((member, index) => (
                                        <StyledTableRow className="animate-opacity" key={`member-${index}`}>
                                            <StyledTableCell>
                                                <Checkbox
                                                    checked={selectedMembers.includes(index)}
                                                    onChange={() => handleSelectMember(index)}
                                                    inputProps={{ 'aria-label': `select member ${index}` }}
                                                    sx={{ padding: 0 }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>{member?.gpid || ''}</StyledTableCell>
                                            <StyledTableCell>{`${member.firstName || ''} ${member?.lastName || ''}`.trim() || '-'}</StyledTableCell>
                                            <StyledTableCell>{member?.email || '-'}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {selectedMembers.length > 0 && (
                            <>
                                <Box
                                    sx={{
                                        backgroundColor: '#FFE6E6',
                                        padding: '12px 16px',
                                        marginTop: '16px',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#000000' }}>
                                        {selectedMembers.length} members selected for removal
                                    </Typography>
                                </Box>
                                <Box display="flex" flexDirection="row-reverse" gap={1} sx={{ mt: 2, alignItems: 'baseline' }}>
                                    <Box sx={{ marginTop: '0px' }}>
                                        <CancelRequest />
                                    </Box>
                                    <SubmitRequest />
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ADGroupMembersRemoval;
